import React, {Dispatch, SetStateAction} from "react";
import Button from "@awsui/components-react/button";
import {useMutation} from "react-query";
import {FlashbarProps} from "@awsui/components-react/flashbar/interfaces";
import getRiderSynonyms from "api/getRiderSynonyms";
import RiderAndSynonyms from "model/RiderAndSynonyms";
import RiderSynonym from "model/RiderSynonym";
import {useAuth} from "hooks/useAuth";


interface SearchRiderSynonymsButtonProps {
    riderName: string;
    buttonEnabled: boolean;
    setButtonEnabled: Dispatch<SetStateAction<boolean>>;
    setSubmitMessage: Dispatch<SetStateAction<FlashbarProps.MessageDefinition | undefined>>;
    setCandidates: Dispatch<SetStateAction<RiderSynonym[] | undefined>>;
}

const SearchRiderSynonymsButton: React.FC<SearchRiderSynonymsButtonProps> = (props) => {

    const riderName = props.riderName;

    const [{user}] = useAuth();

    const {
        mutate: searchRiderSynonymsMutation,
        isLoading: searchRiderSynonymsIsLoading
    } = useMutation<RiderAndSynonyms[], Error, string>(
        (name: string) => getRiderSynonyms([{name: name}], user?.token), {
            onSuccess: (riderSynonyms: RiderAndSynonyms[], name: string) => {
                // The endpoint omits any submitted name that already resolves, so an
                // empty array is the signal that this spelling needs no synonym at all
                // — not that the search failed.
                if (riderSynonyms.length === 0) {
                    props.setCandidates(undefined);
                    props.setSubmitMessage({
                        header: "Name already resolves",
                        type: "info",
                        content: `"${name}" already resolves to a rider — no synonym needed.`,
                    })
                    return;
                }
                const candidates = riderSynonyms[0].synonyms;
                if (candidates.length === 0) {
                    props.setCandidates(undefined);
                    props.setSubmitMessage({
                        header: "No candidate riders found",
                        type: "info",
                        content: `No existing rider resembles "${name}". Add the rider first.`,
                    })
                    return;
                }
                props.setCandidates(candidates);
                props.setSubmitMessage(undefined);
            },
            onError: () => {
                props.setSubmitMessage({
                    header: "Error when getting rider synonyms",
                    type: "error",
                    content: "Try to refresh the page.",
                })
            },
            onSettled: () => {
                props.setButtonEnabled(false);
                // Deliberately NOT clearing riderName here (unlike the bulk page's
                // GetRiderSynonymsButton): the typed spelling IS the `synonym` value
                // that the follow-up PUT needs to send.
            }
        }
    );

    return (
        <Button
            disabled={!props.buttonEnabled || riderName.trim() === ""}
            iconAlign="left"
            loading={searchRiderSynonymsIsLoading}
            variant="primary"
            onClick={() => searchRiderSynonymsMutation(riderName.trim())}
        >
            Search
        </Button>
    );
};

export default SearchRiderSynonymsButton;
